import type { UpdateThumbnail } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import { uploadThumbnail } from '#~/control/preset/index.js';
import { StudioError } from '#~/error/index.js';

/**
 * Update the thumbnail associated with the specified preset.
 *
 * @param deviceId - The ID of the device for which a preset is associated.
 * @param presetId - The ID of the preset whose thumbnail is to be updated.
 * @param token - The token.
 */
export const updateThumbnail: UpdateThumbnail = async (
  deviceId,
  presetId,
  token
): Promise<void> => {
  try {
    const proxy = locateProxy(deviceId);

    if (!proxy) {
      throw new Error('CP-914');
    }

    // Upload a thumbnail image for this preset.
    const thumbnailPromise = uploadThumbnail(proxy.id, deviceId, presetId, token);

    // Wait for thumbnail to be uploaded.
    const thumbnail = await thumbnailPromise;

    // Define mutation.
    const thumbnailMutation = {
      thumbnail
    };

    // Send mutation.
    await proxy.mutate(
      {
        control: {
          studio: {
            device: {
              [deviceId]: {
                control: {
                  presets: {
                    items: {
                      [presetId]: thumbnailMutation
                    }
                  }
                }
              }
            }
          }
        }
      },
      {}
    );

    await proxy.save(token);
  } catch (cause) {
    throw new StudioError('Unable to update preset.', { cause });
  }
};
